import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Upload, X } from 'lucide-react'
import { useAuth } from '../../shared/auth'
import { userKanriService } from '../services/userKanriService'
import { useApiError } from '../../shared/hooks/useApiError'
import { useBlobUrlFromApi } from '../hooks/useBlobUrl'
import Button from '../../shared/ui/Button'
import MessageDialog from '../../shared/ui/MessageDialog'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Layout from '../components/Layout'

// Validation schema for user update form
const userUpdateSchema = z.object({
  userName: z.string().min(1, 'ユーザー名は必須項目です'),
  mailAddress: z.string().min(1, 'メールアドレスは必須項目です').email('有効なメールアドレスを入力してください'),
  loginPassword: z.string().optional(),
  loginPassword2: z.string().optional()
}).superRefine((data, ctx) => {
  // If password is provided, confirmation is required
  if (data.loginPassword && data.loginPassword.trim() !== '') {
    if (!data.loginPassword2 || data.loginPassword2.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        message: '確認用パスワードを入力してください',
        path: ['loginPassword2']
      })
    } else if (data.loginPassword !== data.loginPassword2) {
      ctx.addIssue({
        code: 'custom',
        message: '確認用パスワードが一致しません',
        path: ['loginPassword2']
      })
    }
  }
})

const UserInfo = () => {
  const { user } = useAuth()
  const [userData, setUserData] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPassword2, setShowPassword2] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const fileInputRef = useRef(null)

  const {
    handleApiError,
    handleApiResponse,
    errorMessage,
    closeErrorDialog,
    showSuccessMessage
  } = useApiError()

  // Form setup
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(userUpdateSchema),
    mode: 'onSubmit'
  })

  // Profile image fetch function - memoized to prevent unnecessary re-renders
  const fetchProfileImage = useCallback(async () => {
    if (!userData?.profileImageId) return null
    
    try {
      return await userKanriService.getProfileImage(
        userData.pixUserId,
        userData.profileImageId,
        userData.filenameExtension
      )
    } catch (error) {
      console.error('Failed to load profile image:', error)
      return null
    }
  }, [userData?.pixUserId, userData?.profileImageId, userData?.filenameExtension])

  // Profile image blob URL management with automatic cleanup
  const { blobUrl: profileImageUrl, isLoading: isProfileImageLoading } = useBlobUrlFromApi(
    userData?.profileImageId ? fetchProfileImage : null,
    // Dependencies - only refetch when user data changes
    [userData?.pixUserId, userData?.profileImageId, userData?.filenameExtension],
    // MIME type
    'image/jpeg'
  )

  const loadUserData = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await userKanriService.getPixUserList({ pixUserId: user })
      if (handleApiResponse(response, 'ユーザー情報の読み込みに失敗しました。')) {
        return
      }
      if (response.returnCode === '200' && response.pixUserList && response.pixUserList.length > 0) {
        const userInfo = response.pixUserList[0]
        setUserData(userInfo)
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      setIsLoading(false)
    }
  }, [handleApiError, handleApiResponse, user])

  // Load user data on component mount
  useEffect(() => {
    loadUserData()
  }, [loadUserData])

  // Set form values when modal opens
  useEffect(() => {
    if (isModalOpen && userData) {
      reset({
        userName: userData.userName || '',
        mailAddress: userData.mailAddress || '',
        loginPassword: '',
        loginPassword2: ''
      })
    }
  }, [isModalOpen, userData, reset])


  const handleUpdateClick = () => {
    if (userData) {
      setIsModalOpen(true)
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    reset()
    setShowPassword(false)
    setShowPassword2(false)
  }

  const handleProfileImageClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    try {
      setIsUpdating(true)
      const formData = new FormData()
      formData.append('inputFile', file)
      formData.append('fileName', file.name)
      formData.append('fileSize', file.size)

      const response = await userKanriService.uploadProfileImage(formData)

      // Check if response has error
      if (handleApiResponse(response)) {
        return // Error was handled, exit early
      }
      
      // Reload user data to get updated profile image
      await loadUserData()
      showSuccessMessage('プロフィール画像を更新しました。')
    } catch (error) {
      handleApiError(error)
    } finally {
      setIsUpdating(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const onSubmit = async (formData) => {
    try {
      setIsUpdating(true)

      // Check if email is already taken by another user
      if (formData.mailAddress !== userData.mailAddress) {
        const countResponse = await userKanriService.getPixUserCount(formData.mailAddress)
        if (countResponse.pixUserCount !== '0') {
          handleApiError(new Error('このメールアドレスは既に登録されています。'), 'このメールアドレスは既に登録されています。')
          return
        }
      }

      // Update user data
      const updateData = {
        use_init: 'true',
        action: 'add',
        pageAction: 'updateInfo',
        pixUserId: userData.pixUserId,
        userName: formData.userName,
        loginPassword: formData.loginPassword || null,
        mailAddress: formData.mailAddress,
        pixtagRiyoFlg: userData.pixtagRiyoFlg === '1',
        riyoKyotenId: userData.riyoKyotenId,
        riyoKahi: Number(userData.riyoKahi),
        riyoKengen: Number(userData.userAuthority)
      }

      const response = await userKanriService.updatePixUser(updateData)

      // Check if response has error
      if (handleApiResponse(response)) {
        return // Error was handled, exit early
      }
      
      showSuccessMessage('登録が完了しました。')
      handleModalClose()
      await loadUserData() // Reload user data
    } catch (error) {
      handleApiError(error)
    } finally {
      setIsUpdating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" role="status" aria-label="Loading"></div>
      </div>
    )
  }

  if (!userData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">ユーザー情報を読み込めませんでした。</p>
      </div>
    )
  }

  return (
    <Layout pageTitle="ユーザー情報">
      <div className="bg-white p-6 mx-4 my-2">
        {/* Profile Image */}
        <div className="text-center mb-6">
          <div className="relative inline-block">
            <div
              className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity border border-gray-300"
              onClick={handleProfileImageClick}
            >
              {isProfileImageLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" role="status" aria-label="Loading profile image"></div>
                </div>
              ) : (
                <img
                  src={profileImageUrl || '/core/img/noprofileimage.png'}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/core/img/noprofileimage.png'
                  }}
                />
              )}
            </div>
            <div 
              className="absolute bottom-0 right-0 bg-primary-600 rounded-full p-1 cursor-pointer hover:bg-primary-700 transition-colors"
              onClick={handleProfileImageClick}
            >
              <Upload className="h-4 w-4 text-white" />
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* User Info Table */}
        <div className="mb-6 flex justify-center">
          <table className="w-full max-w-2xl border-collapse border border-gray-300">
            <tbody>
              <tr>
                <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300 w-1/3">
                  ユーザー名
                </th>
                <td className="p-3 border border-gray-300 w-2/3">
                  <span className="readRow">{userData.userName}</span>
                </td>
              </tr>
              <tr>
                <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300">
                  メールアドレス
                </th>
                <td className="p-3 border border-gray-300">
                  <span className="readRow">{userData.mailAddress}</span>
                </td>
              </tr>
              <tr>
                <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300">
                  pix-label利用状況
                </th>
                <td className="p-3 border border-gray-300">
                  <span className="readRow">{userData.pixtagRiyo || '利用中'}</span>
                </td>
              </tr>
              <tr>
                <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300">
                  利用拠点
                </th>
                <td className="p-3 border border-gray-300">
                  <span className="readRow">{userData.riyoKyoten || ''}</span>
                </td>
              </tr>
              <tr>
                <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300">
                  利用権限
                </th>
                <td className="p-3 border border-gray-300">
                  <span className="readRow">{userData.riyoKengen}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Update Button */}
        <div className="text-center">
          <Button
            onClick={handleUpdateClick}
            className="px-6 py-2"
            disabled={isUpdating}
          >
            ユーザー情報更新
          </Button>
        </div>

        {/* Update User Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">ユーザー情報更新</h3>
                <button
                  onClick={handleModalClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Profile Image in Modal */}
              <div className="text-center mb-4">
                <div className="relative inline-block">
                  <div
                    className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-2 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity border border-gray-300"
                    onClick={handleProfileImageClick}
                  >
                    {isProfileImageLoading ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" role="status" aria-label="Loading profile image"></div>
                      </div>
                    ) : (
                      <img
                        src={profileImageUrl || '/core/img/noprofileimage.png'}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/core/img/noprofileimage.png'
                        }}
                      />
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600">画像クリックで画像設定可能</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                <table className="w-full border-collapse border border-gray-300">
                  <tbody>
                    <tr>
                      <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300 w-1/3">
                        ユーザー名
                      </th>
                      <td className="p-3 border border-gray-300 w-2/3">
                        <input
                          type="text"
                          {...register('userName')}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        {errors.userName && (
                          <p className="text-red-500 text-sm mt-1">{errors.userName.message}</p>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300">
                        メールアドレス
                      </th>
                      <td className="p-3 border border-gray-300">
                        <input
                          type="text"
                          {...register('mailAddress')}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        {errors.mailAddress && (
                          <p className="text-red-500 text-sm mt-1">{errors.mailAddress.message}</p>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300">
                        パスワード
                      </th>
                      <td className="p-3 border border-gray-300 relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          {...register('loginPassword')}
                          className="w-full px-2 py-1 pr-8 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="変更する場合のみ入力"
                          data-testid="password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          data-testid="password-toggle-button"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <th className="bg-gray-100 p-3 text-left font-normal border border-gray-300">
                        パスワード(確認)
                      </th>
                      <td className="p-3 border border-gray-300 relative">
                        <input
                          type={showPassword2 ? 'text' : 'password'}
                          {...register('loginPassword2')}
                          className="w-full px-2 py-1 pr-8 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="変更する場合のみ入力"
                          data-testid="password-confirm-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword2(!showPassword2)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          data-testid="password-confirm-toggle-button"
                        >
                          {showPassword2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        {errors.loginPassword2 && (
                          <p className="text-red-500 text-sm mt-1">{errors.loginPassword2.message}</p>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>


                <div className="flex space-x-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={isUpdating}
                  >
                    {isUpdating ? '更新中...' : '更新'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleModalClose}
                    className="flex-1"
                    disabled={isUpdating}
                  >
                    キャンセル
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Error Dialog */}
        <MessageDialog
          isOpen={!!errorMessage}
          onClose={closeErrorDialog}
          title="メッセージ"
          message={errorMessage}
        />
      </div>
    </Layout>
  )
}

export default UserInfo
